import { Grid, Typography } from "@material-ui/core";
import { COPY } from "../constants";
import {
  civilStatusItems,
  genderItems,
  nationalityItems,
  suffixItems,
} from "../options";
import { hasValue as hasIdValue } from "../logic/idValidation";
import SectionCard from "../ui/SectionCard";
import { FileTextIcon, UserIcon } from "../ui/icons";
import DateField from "../ui/fields/DateField";
import LockedField from "../ui/fields/LockedField";
import PhoneFieldPH from "../ui/fields/PhoneFieldPH";
import SegmentedPills from "../ui/fields/SegmentedPills";
import SelectField from "../ui/fields/SelectField";
import TextField from "../ui/fields/TextField";
import IdDocuments from "./IdDocuments";
import MarriageCertificateUpload from "./MarriageCertificateUpload";
import { StepViewProps } from "./types";

export default function ClientInformation({
  state,
  isTier3,
  handlers,
}: StepViewProps) {
  const { draft, errors } = state;
  const kyc = draft.kyc;
  const name = kyc.name || {};
  const err = (path: string) => errors[path];

  // Name lives in both user.* and kyc.name.*; keep them in sync so the Tier 3
  // payload (kyc.name) and the user payload agree.
  const setName = (part: "firstName" | "lastName", value: string) => {
    handlers.setKycField(`name.${part}`, value);
    handlers.setUserField(part, value);
  };

  const ids = kyc.ids || {};
  const originalIds = state.originalKyc.ids || {};
  const hasTinOnFile = hasIdValue(originalIds.TIN);
  const hasSssOnFile = hasIdValue(originalIds.SSS);
  const hasTinSssOnFile = hasTinOnFile || hasSssOnFile;
  const hasTin = hasTinOnFile || hasIdValue(ids.TIN);
  const setTaxId = (field: "TIN" | "SSS", value: string) => {
    handlers.setKycField(`ids.${field}`, value);
  };

  const idGroup = (
    <SectionCard
      title="Government ID"
      icon={<FileTextIcon size={18} />}
      verified={hasTinSssOnFile}
      helperNote={
        hasTinSssOnFile
          ? "Identification numbers already on file can only be viewed."
          : undefined
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {hasTinOnFile ? (
            <LockedField label="TIN" value={ids.TIN || originalIds.TIN} />
          ) : (
            <TextField
              label="TIN"
              value={ids.TIN}
              onChange={(v) => setTaxId("TIN", v)}
            />
          )}
          {!hasTin && (
            <Typography
              component="p"
              variant="caption"
              color="textSecondary"
              style={{ margin: "6px 0 0" }}
            >
              Note: TIN number is required by certain partners to invest in
              select funds.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {hasSssOnFile ? (
            <LockedField
              label="SSS / GSIS"
              value={ids.SSS || originalIds.SSS}
            />
          ) : (
            <TextField
              label="SSS / GSIS"
              value={ids.SSS}
              onChange={(v) => setTaxId("SSS", v)}
            />
          )}
        </Grid>
      </Grid>
      <IdDocuments state={state} handlers={handlers} />
    </SectionCard>
  );

  // Tier 2 and Tier 3 share personal details; email stays locked for both.
  return (
    <>
      <SectionCard
        title="Personal information"
        icon={<UserIcon size={18} />}
        helperNote={COPY.lockedHelper}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First name"
              value={name.firstName ?? draft.user.firstName}
              onChange={(v) => setName("firstName", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Middle name"
              value={name.middleName}
              onChange={(v) => handlers.setKycField("name.middleName", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last name"
              value={name.lastName ?? draft.user.lastName}
              onChange={(v) => setName("lastName", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Suffix"
              value={name.suffix}
              items={suffixItems}
              onChange={(v) => handlers.setKycField("name.suffix", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LockedField
              label="Email address"
              value={kyc.email ?? draft.user.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneFieldPH
              label="Mobile number"
              required
              value={draft.user.mobileNumber}
              error={err("user.mobileNumber")}
              onChange={(v) => handlers.setUserField("mobileNumber", v)}
            />
          </Grid>
          <Grid item xs={12}>
            <SegmentedPills
              label="Gender"
              required
              value={kyc.gender}
              options={genderItems}
              error={err("gender")}
              onChange={(v) => handlers.setKycField("gender", v)}
            />
          </Grid>
          <Grid item xs={12}>
            <SegmentedPills
              label="Civil status"
              required
              value={kyc.civil?.civilStatus}
              options={civilStatusItems}
              error={err("civil.civilStatus")}
              onChange={(v) => handlers.setKycField("civil.civilStatus", v)}
            />
          </Grid>
          <MarriageCertificateUpload state={state} handlers={handlers} />
          <Grid item xs={12} sm={6}>
            <DateField
              label="Date of birth"
              value={kyc.dateOfBirth}
              onChange={(v) => handlers.setKycField("dateOfBirth", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Place of birth"
              value={kyc.placeOfBirth}
              onChange={(v) => handlers.setKycField("placeOfBirth", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City of birth"
              value={kyc.cityOfBirth}
              onChange={(v) => handlers.setKycField("cityOfBirth", v)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label="Nationality"
              value={kyc.nationality}
              items={nationalityItems}
              onChange={(v) => handlers.setKycField("nationality", v)}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {isTier3 && idGroup}
    </>
  );
}
